import { plural } from '../../../../shared/helpers/plural';
import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';
import type { AddUsersToTeamRequest, AddUsersToTeamResponse } from '../../../../shared/types/routes/teams';
import type { Team } from '../../../types';

import { getTeamQueryKey } from './useTeam';

type AddUsersToTeamVariables = {
  teamId: number;
  users: Array<{ id: number; roleId?: number }>;
};

//  Actual function to add users to a team
async function addUsersToTeam({ teamId, users }: AddUsersToTeamVariables) {
  const response = await put<AddUsersToTeamRequest, AddUsersToTeamResponse>(`/api/teams/${teamId}/users`, { users, action: 'add' });
  return response.data as AddUsersToTeamResponse;
}

/**
 * Hook for adding users to a team.
 */
export default function useAddUsersToTeam() {
  return useToastedMutation({
    mutationFn: addUsersToTeam,
    successToastMessage: (_response, variables: AddUsersToTeamVariables) =>
      `Added ${variables.users.length} ${plural(variables.users.length, 'user')} to team.`,
    errorToastMessage: 'Failed to add users to team.',
    onSuccess: (response, variables: AddUsersToTeamVariables) => {
      // Update the team's user count in the cache
      queryClient.setQueryData(getTeamQueryKey(variables.teamId), (oldTeam?: Team) => {
        if (!oldTeam) {
          return oldTeam;
        }
        return {
          ...oldTeam,
          teamUsers: response.users,
        };
      });

      // Also re-fetch the team to get any other changes
      queryClient.invalidateQueries({ queryKey: getTeamQueryKey(variables.teamId) });
    },
  });
}
